import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  LuLeaf, LuDroplets, LuMoon, LuGlassWater, LuWind, LuStethoscope, LuThermometer, LuPhone, LuArrowRight,
  LuChevronDown, LuSparkles,
} from 'react-icons/lu';
import { useLanguage } from '../../contexts/LanguageContext';
import { scrollToTop } from '../../utils/scrollUtils';
import { fetchProductDetails } from '../../services/apiService';
import ProductCard from '../Shop/Products/ProductCard';
import { ProductCardSkeleton } from '../Shop/Products/ProductGrid';
import { GUTTER, PANEL_GAP, DOT_GRID_STYLE } from './landingUi';

const EASE = [0.22, 1, 0.36, 1];
// Autumn accent — amber, used only for small touches (leaves, the season
// chip, the warning dots). The brand red stays for the title and the CTA.
const AMBER = '#C8702A';
const IMG = {
  blanket: '/images/autumn/flu-blanket.jpg',
  care: '/images/autumn/flu-care.jpg',
};
const TIP_ICONS = [LuDroplets, LuMoon, LuGlassWater, LuWind];

// The two columns of the cold/flu comparison. Cold is the calm, neutral one;
// flu is the more serious one, so it carries the brand blush and red heading.
const COMPARE_COLUMNS = [
  { key: 'cold', Icon: LuWind, cell: 'bg-[#F6F3F0]', head: 'text-gray-800', value: 'font-medium text-gray-600' },
  { key: 'flu', Icon: LuThermometer, cell: 'bg-brand-tint', head: 'text-brand-deep', value: 'font-semibold text-gray-900' },
];

// The season's featured products, by pharmacode, in display order. Edit this
// list to change what the section promotes.
const PROMO_PHARMACODES = ['7752479', '7241014', '4868528'];

// A few leaves drifting down the panel: position, size, colour, timing.
const LEAVES = [
  { left: '4%', size: 76, color: '#D9822B', delay: 0, dur: 15 },
  { left: '19%', size: 58, color: '#B5532A', delay: 4, dur: 18 },
  { left: '41%', size: 70, color: '#E0A33A', delay: 8, dur: 16 },
  { left: '63%', size: 54, color: '#C8702A', delay: 2, dur: 20 },
  { left: '79%', size: 84, color: '#B5532A', delay: 6, dur: 17 },
  { left: '92%', size: 62, color: '#E0A33A', delay: 11, dur: 19 },
];

const Leaf = ({ size, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
    <path d="M12 2c.6 2 1.8 3.2 3.6 3.4-.6 1-.7 1.9-.2 2.7 1.3-.5 2.6-.4 3.9.3-.9 1.2-1 2.4-.4 3.6-1.5 0-2.7.7-3.4 2l1.9 1.6c-1.7.5-3.2.4-4.4-.4V22h-1.9v-6.8c-1.2.8-2.7.9-4.4.4l1.9-1.6c-.7-1.3-1.9-2-3.4-2 .6-1.2.5-2.4-.4-3.6 1.3-.7 2.6-.8 3.9-.3.5-.8.4-1.7-.2-2.7C10.2 5.2 11.4 4 12 2z" />
  </svg>
);

// Resolve the promo pharmacodes to articles. A product that fails to load is
// simply not shown.
const loadPromoArticles = async () => {
  const results = await Promise.all(
    PROMO_PHARMACODES.map(async (code) => {
      try {
        const res = await fetchProductDetails(code);
        return res?.data?.product?.articles?.find((a) => String(a.pharmacode) === String(code)) || null;
      } catch {
        return null;
      }
    })
  );
  return results.filter(Boolean);
};

/**
 * Seasonal guide. Top: cold vs. flu next to the photo collage. Bottom: a
 * compact card with the tips, when to see a doctor and the CTA (left), and
 * the team's featured products for the season as full shop cards (right).
 *
 * Sand like the rest of the page, warmed with a faint amber tint and a few
 * drifting leaves; the brand red appears only in the title and the shop CTA.
 * Cold and flu are compared side by side: a neutral column and a blush one,
 * so the difference reads at a glance.
 *
 * Phones get a compact cut: a short photo banner instead of the collage, the
 * products as a swipeable row, and the doctor list folded behind its title.
 * Desktop shows everything.
 */
const AutumnGuide = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const prefersReduced = useReducedMotion();
  // Small tablets only: the doctor advice folds away behind its title.
  const [doctorOpen, setDoctorOpen] = useState(false);
  const [picks, setPicks] = useState([]);
  const [picksLoading, setPicksLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    loadPromoArticles().then((articles) => {
      if (!cancelled) { setPicks(articles); setPicksLoading(false); }
    });
    return () => { cancelled = true; };
  }, []);

  const a = (key) => t(`home.autumn.${key}`);
  const rows = Array.isArray(a('compare.rows')) ? a('compare.rows') : [];
  const tips = Array.isArray(a('tips.items')) ? a('tips.items') : [];
  const doctorText = Array.isArray(a('doctor.paragraphs')) ? a('doctor.paragraphs') : [];

  const openFluCategory = () => {
    sessionStorage.setItem('germanName', 'Grippe & Erkältung');
    navigate('/categories/FluColds/products');
    setTimeout(() => scrollToTop(), 100);
  };
  const openProduct = (code) => {
    navigate(`/product/${code}`);
    setTimeout(() => scrollToTop(), 100);
  };

  return (
    <section className={`${GUTTER} ${PANEL_GAP}`} aria-label={a('eyebrow')}>
      <style>{`
        /* top runs the full height of the panel (whatever its size);
           the transform adds the sway and the spin. */
        @keyframes autumn-leaf-fall {
          0%   { top: -4%;  transform: translateX(0) rotate(0deg);        opacity: 0; }
          10%  { opacity: 0.55; }
          50%  { transform: translateX(28px) rotate(160deg); }
          90%  { opacity: 0.45; }
          100% { top: 102%; transform: translateX(-12px) rotate(320deg); opacity: 0; }
        }
        .autumn-rail { scrollbar-width: none; } .autumn-rail::-webkit-scrollbar { display: none; }
      `}</style>

      <div
        className="relative overflow-hidden rounded-[2rem] px-[clamp(1.25rem,4vw,4rem)] py-[clamp(1.75rem,4.5vw,4.5rem)] ring-1 ring-black/[0.04] lg:rounded-[2.5rem]"
        style={{ background: 'linear-gradient(135deg, #FBEEE2 0%, #F6F3F0 45%, #F6F3F0 100%)' }}
      >
        {/* Texture + drifting leaves */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={DOT_GRID_STYLE} />
        {!prefersReduced && (
          <div aria-hidden="true" className="pointer-events-none absolute inset-0">
            {LEAVES.map((leaf, i) => (
              <span
                key={i}
                className={`absolute ${i % 2 ? 'hidden lg:block' : ''}`}
                style={{ left: leaf.left, top: '-4%', animation: `autumn-leaf-fall ${leaf.dur}s linear ${leaf.delay}s infinite` }}
              >
                <Leaf size={leaf.size} color={leaf.color} />
              </span>
            ))}
          </div>
        )}

        <div className="relative grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-[clamp(1.5rem,3vw,3.5rem)]">
          {/* ===== Intro + cold/flu comparison ===== */}
          <motion.div
            className="lg:col-span-8"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.55, ease: EASE }}
          >
            {/* Phones: one short banner in place of the photo collage — the
                autumn road, with the two flu photos as round thumbnails. */}
            <div className="relative mb-6 lg:hidden">
              <div className="relative h-36 overflow-hidden rounded-[1.5rem] shadow-[0_20px_40px_-28px_rgba(17,24,39,0.6)] ring-4 ring-white sm:h-48">
                <img src={IMG.blanket} alt={a('imageAlt.blanket')} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover object-[50%_30%]" />
                <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-[11px] font-bold text-gray-700 backdrop-blur">
                  <LuLeaf className="h-3.5 w-3.5" style={{ color: AMBER }} aria-hidden="true" />
                  {a('season')}
                </span>
              </div>
              <img
                src={IMG.care}
                alt=""
                loading="lazy"
                decoding="async"
                className="absolute -bottom-4 right-4 h-14 w-14 rounded-full object-cover object-[60%_55%] shadow-[0_10px_22px_-12px_rgba(17,24,39,0.7)] ring-4 ring-white"
              />
            </div>

            <h2 className="text-[clamp(1.75rem,4.2vw,3.5rem)] font-extrabold leading-[1.02] tracking-[-0.04em] text-gray-900">
              {a('title')} <span className="text-brand">{a('titleHighlight')}</span>
            </h2>
            <p className="mt-3 max-w-xl text-[14px] leading-relaxed text-gray-500 sm:text-[15px] lg:mt-4 xl:text-base">{a('subtitle')}</p>

            {/* Comparison card — both columns side by side. From sm up the
                labels sit in a first column and each illness is one tinted
                band; on phones each symptom gets a small label row above its
                two values so neither column gets squeezed. */}
            <div className="mt-5 rounded-[1.75rem] bg-white p-4 ring-1 ring-black/[0.05] shadow-[0_24px_50px_-36px_rgba(17,24,39,0.5)] sm:p-6 lg:mt-7">
              <h3 className="text-lg font-extrabold tracking-[-0.02em] text-gray-900">{a('compare.title')}</h3>

              <div role="table" aria-label={a('compare.title')} className="mt-4 grid grid-cols-2 gap-x-2 sm:mt-5 sm:grid-cols-[minmax(6.5rem,0.8fr),1fr,1fr]">
                <div role="row" className="contents">
                  <span role="columnheader" className="hidden sm:block" />
                  {COMPARE_COLUMNS.map(({ key, Icon, cell, head }) => (
                    <div
                      key={key}
                      role="columnheader"
                      className={`flex items-center gap-2 rounded-xl px-3 py-2.5 text-[14px] font-extrabold tracking-[-0.01em] sm:rounded-b-none sm:rounded-t-2xl sm:pb-3 sm:pt-3.5 ${cell} ${head}`}
                    >
                      <span className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-white shadow-[0_4px_10px_-6px_rgba(17,24,39,0.5)]" aria-hidden="true">
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      {a(`compare.${key}`)}
                    </div>
                  ))}
                </div>

                {rows.map((row, i) => {
                  const last = i === rows.length - 1;
                  return (
                    <div key={row.label} role="row" className="contents">
                      <div
                        role="rowheader"
                        className="col-span-2 pb-1.5 pt-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400 sm:col-span-1 sm:flex sm:items-center sm:border-t sm:border-black/[0.05] sm:py-2.5 sm:pr-3 sm:text-[13px] sm:normal-case sm:tracking-normal"
                      >
                        {row.label}
                      </div>
                      {COMPARE_COLUMNS.map(({ key, cell, value }) => (
                        <div
                          key={key}
                          role="cell"
                          className={`flex items-center rounded-xl px-3 py-2.5 text-[13.5px] leading-snug sm:rounded-none sm:border-t sm:border-white ${last ? 'sm:rounded-b-2xl sm:pb-3.5' : ''} ${cell} ${value}`}
                        >
                          {row[key]}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>

          {/* ===== Photo collage (desktop) ===== */}
          <motion.div
            // Fixed height, two rows: the photos are portrait, and left to their
            // natural size they stacked into a column taller than the section.
            // Each image fills its cell (absolute + object-cover) instead.
            className="hidden min-h-[30rem] grid-rows-2 gap-4 lg:col-span-4 lg:grid"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6, delay: 0.08, ease: EASE }}
          >
            <figure className="relative overflow-hidden rounded-[1.75rem] shadow-[0_24px_48px_-32px_rgba(17,24,39,0.6)] ring-4 ring-white lg:rotate-[1.5deg]">
              <img src={IMG.blanket} alt={a('imageAlt.blanket')} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover object-[50%_30%]" />
              <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-[11px] font-bold text-gray-700 shadow-[0_8px_20px_-10px_rgba(17,24,39,0.6)] backdrop-blur">
                <LuLeaf className="h-3.5 w-3.5" style={{ color: AMBER }} aria-hidden="true" />
                {a('season')}
              </span>
            </figure>
            <figure className="relative overflow-hidden rounded-[1.75rem] shadow-[0_24px_48px_-32px_rgba(17,24,39,0.6)] ring-4 ring-white lg:-rotate-[1.5deg]">
              <img src={IMG.care} alt={a('imageAlt.care')} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover object-[60%_55%]" />
            </figure>
          </motion.div>
        </div>

        {/* ===== Bottom: compact guide (left) + featured products (right) ===== */}
        <div className="relative mt-6 grid grid-cols-1 gap-4 lg:mt-10 lg:grid-cols-12 lg:gap-[clamp(1.25rem,2.5vw,2.5rem)]">
          {/* Left: tips, doctor and CTA in one tight card */}
          <div className="sm:rounded-[1.75rem] sm:bg-white/80 sm:p-6 sm:ring-1 sm:ring-black/[0.05] sm:backdrop-blur-sm lg:col-span-6 xl:col-span-5">
            <h3 className="hidden text-[11px] font-semibold uppercase tracking-[0.22em] text-gray-400 sm:block">{a('tips.title')}</h3>
            <ul className="mt-3 hidden grid-cols-2 gap-x-4 gap-y-3 sm:grid">
              {tips.map((tip, i) => {
                const Icon = TIP_ICONS[i] || LuLeaf;
                return (
                  <li key={tip.title} className="flex gap-3">
                    <span
                      className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl"
                      style={{ backgroundColor: `${AMBER}17`, color: AMBER }}
                      aria-hidden="true"
                    >
                      <Icon className="h-4 w-4" strokeWidth={1.9} />
                    </span>
                    <div className="min-w-0">
                      <h4 className="text-[14px] font-extrabold leading-tight tracking-[-0.01em] text-gray-900">{tip.title}</h4>
                      <p className="mt-0.5 text-[12.5px] leading-snug text-gray-500">{tip.text}</p>
                    </div>
                  </li>
                );
              })}
            </ul>

            {/* When to see a doctor — folded on phones, open on desktop */}
            <div className="mt-5 hidden rounded-2xl bg-[#F6F3F0] p-4 sm:block">
              <button
                type="button"
                onClick={() => setDoctorOpen((v) => !v)}
                aria-expanded={doctorOpen}
                className="flex w-full items-center gap-2.5 text-left lg:pointer-events-none"
              >
                <LuStethoscope className="h-4 w-4 flex-shrink-0 text-gray-700" strokeWidth={1.9} aria-hidden="true" />
                <span className="text-[14px] font-extrabold text-gray-900">{a('doctor.title')}</span>
                <LuChevronDown className={`ml-auto h-4 w-4 flex-shrink-0 text-gray-500 transition-transform duration-300 lg:hidden ${doctorOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>
              <div className={`mt-2.5 space-y-2 pl-[1.625rem] lg:block ${doctorOpen ? 'block' : 'hidden'}`}>
                {doctorText.map((p, i) => (
                  <p key={i} className={`text-[12.5px] leading-relaxed ${i === 0 ? 'text-gray-700' : 'text-gray-500'}`}>{p}</p>
                ))}
              </div>
            </div>

            <p className="mt-4 hidden text-[13px] font-semibold text-gray-700 sm:block">{a('cta.note')}</p>
            <div className="grid grid-cols-2 gap-2 sm:mt-3">
              <button
                type="button"
                onClick={openFluCategory}
                className="group inline-flex items-center justify-center gap-1.5 rounded-full bg-brand px-4 py-3 text-[13px] font-semibold text-white shadow-[0_12px_28px_-12px_rgb(var(--brand)/0.55)] transition-colors hover:bg-brand-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2"
              >
                {a('cta.shop')}
                <LuArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </button>
              <a
                href="tel:+41616917488"
                className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#F6F3F0] px-4 py-3 text-[13px] font-semibold text-gray-700 ring-1 ring-black/[0.04] transition-colors hover:text-brand"
              >
                <LuPhone className="h-4 w-4" aria-hidden="true" />
                {a('cta.advice')}
              </a>
            </div>
          </div>

          {/* Right: the season's featured products as full shop cards */}
          <div className="flex flex-col lg:col-span-6 xl:col-span-7">
            <div className="mb-3 flex items-center justify-between gap-3 px-1">
              <h3 className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-gray-400">
                <LuSparkles className="h-3.5 w-3.5" style={{ color: AMBER }} aria-hidden="true" />
                {a('picksTitle')}
              </h3>
              <button
                type="button"
                onClick={openFluCategory}
                className="group inline-flex items-center gap-1 text-[13px] font-semibold text-gray-600 transition-colors hover:text-brand"
              >
                {t('productDetails.showAll')}
                <LuArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </button>
            </div>
            <div className="autumn-rail -mx-[clamp(1.25rem,4vw,4rem)] flex flex-1 snap-x snap-mandatory gap-3 overflow-x-auto px-[clamp(1.25rem,4vw,4rem)] pb-1 sm:mx-0 sm:grid sm:grid-cols-3 sm:overflow-visible sm:px-0 sm:pb-0">
              {picksLoading
                ? PROMO_PHARMACODES.map((code) => (
                    <div key={code} className="w-[46%] flex-shrink-0 snap-start sm:w-auto"><ProductCardSkeleton /></div>
                  ))
                : picks.map((article) => (
                    <div key={article.pharmacode} className="w-[46%] flex-shrink-0 snap-start sm:w-auto">
                      <ProductCard article={article} favorite={{}} onFavoriteClick={() => {}} onProductClick={openProduct} isLoggedIn={false} />
                    </div>
                  ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AutumnGuide;
